import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import './App.css';
import LoginPage from './components/LoginPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import MainLayout from './components/MainLayout';
import LoadingSpinner from './components/shared/LoadingSpinner';

// Lazy-loaded feature pages
const Dashboard = React.lazy(() => import('./components/dashboard/Dashboard'));
const CustomerListPage = React.lazy(() => import('./components/customers/CustomerListPage'));
const CustomerDetailPage = React.lazy(() => import('./components/customers/CustomerDetailPage'));
const ProductListPage = React.lazy(() => import('./components/products/ProductListPage'));
const ProductDetailPage = React.lazy(() => import('./components/products/ProductDetailPage'));
const LicenseListPage = React.lazy(() => import('./components/licenses/LicenseListPage'));
const LicenseDetailPage = React.lazy(() => import('./components/licenses/LicenseDetailPage'));
const UserListPage = React.lazy(() => import('./components/users/UserListPage'));
const UserDetailPage = React.lazy(() => import('./components/users/UserDetailPage'));
const DownloadsPage = React.lazy(() => import('./components/downloads/DownloadsPage'));
const SigningKeyListPage = React.lazy(() => import('./components/signingKeys/SigningKeyListPage'));
const SigningKeyDetailPage = React.lazy(() => import('./components/signingKeys/SigningKeyDetailPage'));
const ReportsPage = React.lazy(() => import('./components/reports/ReportsPage'));
const SupportTicketListPage = React.lazy(() => import('./components/supportTickets/SupportTicketListPage'));
const SupportTicketDetailPage = React.lazy(() => import('./components/supportTickets/SupportTicketDetailPage'));
const LoginCallback = React.lazy(() => import('./components/LoginCallback'));
const CreateAccountPage = React.lazy(() => import('./components/CreateAccountPage'));

const ProtectedRoute: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      </div>
    );
  }

  return isAuthenticated ? (
    <Outlet />
  ) : <Navigate to="/login" />;
};

const RoleRoute: React.FC<{ allowedRoles: string[] }> = ({ allowedRoles }) => {
  const groupsStr = sessionStorage.getItem('groups');
  let groups: string[] = [];
  try {
    groups = groupsStr ? JSON.parse(groupsStr) : [];
  } catch { /* ignore */ }

  const hasAccess = allowedRoles.some(role => groups.includes(role));
  return hasAccess ? <Outlet /> : <Navigate to="/" />;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" /> : <LoginPage />} />
      <Route path="/login/callback" element={
        <Suspense fallback={<LoadingSpinner />}><LoginCallback /></Suspense>
      } />
      <Route path="/create-account" element={
        <Suspense fallback={<LoadingSpinner />}><CreateAccountPage /></Suspense>
      } />
      <Route element={<ProtectedRoute />}>
        <Route element={<MainLayout />}>
          <Route path="/" element={
            <Suspense fallback={<LoadingSpinner />}><Dashboard /></Suspense>
          } />

          {/* Licenses - all authenticated users */}
          <Route path="/licenses" element={
            <Suspense fallback={<LoadingSpinner />}><LicenseListPage /></Suspense>
          } />
          <Route path="/licenses/:id" element={
            <Suspense fallback={<LoadingSpinner />}><LicenseDetailPage /></Suspense>
          } />

          {/* Downloads - all authenticated users */}
          <Route path="/downloads" element={
            <Suspense fallback={<LoadingSpinner />}><DownloadsPage /></Suspense>
          } />

          {/* Support Tickets - all authenticated users */}
          <Route path="/support-tickets" element={
            <Suspense fallback={<LoadingSpinner />}><SupportTicketListPage /></Suspense>
          } />
          <Route path="/support-tickets/:id" element={
            <Suspense fallback={<LoadingSpinner />}><SupportTicketDetailPage /></Suspense>
          } />

          {/* Customers - site-admin only */}
          <Route element={<RoleRoute allowedRoles={['site-admin']} />}>
            <Route path="/customers" element={
              <Suspense fallback={<LoadingSpinner />}><CustomerListPage /></Suspense>
            } />
            <Route path="/customers/:id" element={
              <Suspense fallback={<LoadingSpinner />}><CustomerDetailPage /></Suspense>
            } />
          </Route>

          {/* Signing Keys - site-admin only */}
          <Route element={<RoleRoute allowedRoles={['site-admin']} />}>
            <Route path="/signing-keys" element={
              <Suspense fallback={<LoadingSpinner />}><SigningKeyListPage /></Suspense>
            } />
            <Route path="/signing-keys/:id" element={
              <Suspense fallback={<LoadingSpinner />}><SigningKeyDetailPage /></Suspense>
            } />
          </Route>

          {/* Reports - site-admin only */}
          <Route element={<RoleRoute allowedRoles={['site-admin']} />}>
            <Route path="/reports" element={
              <Suspense fallback={<LoadingSpinner />}><ReportsPage /></Suspense>
            } />
          </Route>

          {/* Products - site-admin only */}
          <Route element={<RoleRoute allowedRoles={['site-admin']} />}>
            <Route path="/products" element={
              <Suspense fallback={<LoadingSpinner />}><ProductListPage /></Suspense>
            } />
            <Route path="/products/:id" element={
              <Suspense fallback={<LoadingSpinner />}><ProductDetailPage /></Suspense>
            } />
          </Route>

          {/* Users - site-admin and org-admin */}
          <Route element={<RoleRoute allowedRoles={['site-admin', 'org-admin']} />}>
            <Route path="/users" element={
              <Suspense fallback={<LoadingSpinner />}><UserListPage /></Suspense>
            } />
            <Route path="/users/:id" element={
              <Suspense fallback={<LoadingSpinner />}><UserDetailPage /></Suspense>
            } />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
